import { SxProps } from '@mui/material';
import theme from '../theme';
import { DataTableColumnProps } from './DataTableColumnProps';

export const groupHeaderRow: SxProps = {
  border: 'none !important',
  cursor: 'pointer',
  backgroundColor: theme.palette.secondary.light,
};

export const groupHeaderArrowIcon = (isOpen: boolean | undefined): SxProps => {
  return {
    transform: `rotate(${isOpen ? '-180deg' : '0'})`,
    transition: 'transform 0.1s',
  };
};

export const tableHeaderCell = <T>(
  childProps: DataTableColumnProps<T>,
  sorting = false,
  drawBottomBorder = false
): SxProps => {
  const style: SxProps = {
    display: {
      md: childProps.hidden ? 'none' : 'table-cell',
    },
    textAlign: {
      md: childProps.textAlign ?? 'left',
      sm: 'left',
    },
  };
  if (sorting && !childProps.disableSort) style.cursor = 'pointer';
  if (drawBottomBorder) style.borderBottom = '1px solid #BBBBBBBB !important';
  return {
    ...style,
    ...childProps.otherStyling,
  };
};

export const tableBodyRow = (
  hasError: boolean | undefined,
  isClickable = false,
  isPartOfGroup = false,
  isOpen: boolean | undefined = false,
  mobileBorder: string | undefined = undefined
): SxProps => {
  const styleBodyRow: SxProps = {
    paddingTop: {
      xs: '1em',
    },
    paddingBottom: {
      xs: '0.5em',
    },
    border: {
      md: 'inherit',
      xs: mobileBorder,
    },
    borderBottom: {
      md: isOpen
        ? `5px solid ${theme.palette.common.white} !important`
        : 'none !important',
    },
    visibility: !isPartOfGroup || isOpen ? 'visible' : 'collapse',
    display: {
      md: 'table-row',
      xs: !isPartOfGroup || isOpen ? 'flex' : 'none',
    },
  };
  if (isClickable) styleBodyRow.cursor = 'pointer';
  if (hasError)
    styleBodyRow.backgroundColor = theme.palette.error.light + ' !important';
  return {
    ...styleBodyRow,
    '&.active-item': {
      border: {
        md: 'inherit',
        xs: mobileBorder,
      },
    },
    '&.alternate-color': {
      backgroundColor: isPartOfGroup ? theme.palette.common.white : undefined,
    },
  };
};

export const tableBodyCell = <T>(
  childProps: DataTableColumnProps<T>,
  index: number,
  hasMenuItems: boolean | undefined = false,
  isActive = false,
  disableActiveItem = false,
  isPartOfGroup = false,
  isOpen: boolean | undefined = false
): SxProps => {
  const style: SxProps = {
    display: {
      xs:
        childProps.mobileHidden &&
        (!isActive || !childProps.mobileVisibleWhenActive)
          ? 'none'
          : childProps.mobileInlineHeaderDirection
          ? 'flex'
          : 'table-cell',
      md: childProps.hidden ? 'none' : 'table-cell',
    },
    color: {
      xs:
        index === 0 && isActive && !disableActiveItem
          ? 'secondary.main'
          : 'inherit',
      md: 'inherit',
    },
    flexBasis: {
      xs:
        childProps.mobileWidth ||
        (index === 0 && hasMenuItems ? '75%' : '100%'),
      md: 'unset',
    },
    fontWeight: {
      xs: index === 0 ? 'bold' : 'unset',
      md: 'unset',
    },
    height: {
      xs: childProps.mobileHeight,
      md: 'unset',
    },
    order: {
      xs: childProps.mobileOrder ?? index - 1,
      md: 'unset',
    },
    textAlign: {
      md: childProps.textAlign ?? 'left',
      xs: childProps.mobileTextAlign ?? 'left',
    },
  };
  if (isPartOfGroup && !isOpen) style.border = 'none !important';
  return {
    ...style,
    ...childProps.otherStyling,
  };
};

export const tableBodyCellCaption = <T>(
  childProps: DataTableColumnProps<T>
): SxProps => {
  const style: SxProps = {
    display: { xs: 'block', md: 'none' },
    alignSelf: {
      xs: childProps.mobileInlineHeaderDirection ? 'center' : 'inherit',
      md: 'inherit',
    },
    order: childProps.mobileInlineHeaderDirection === 'rtl' ? 1 : 0,
    flexGrow: childProps.mobileInlineHeaderDirection === 'ltr' ? 10 : 0,
  };
  return {
    ...style,
    ...childProps.otherStyling,
  };
};

export const tableBodyCellMenuItem = (isVisible: boolean): SxProps => {
  return {
    flexBasis: {
      xs: '25%',
      md: 'unset',
    },
    height: 0,
    order: -1,
    textAlign: 'right',
    '& button': {
      visibility: isVisible ? 'visible' : 'hidden',
    },
  };
};

export const tableBodyCollapsibleCell = (
  hasError: boolean | undefined
): SxProps => {
  const style: SxProps = {
    position: { xs: 'fixed', md: 'static' },
    left: 0,
    bottom: -1,
    width: '100%',
    zIndex: 5000,
  };
  if (hasError)
    style.backgroundColor = theme.palette.error.light + ' !important';
  return style;
};

export const noData: SxProps = { mt: 4, mb: 4 };
